import { useCallback, useEffect, useMemo, useState } from "react";
import { AlertCircle, Loader2, Pencil, Plus, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { DespachoInfoCallout, DespachoSectionCard, DespachoSimpleTable } from "@/components/despacho/DespachoUi";
import MessageEmojiTextarea from "@/components/despacho/MessageEmojiTextarea";
import { despachoFiscalInfoApi, parseFiscalInfoError } from "@/modules/despacho/despachoFiscalInfoApi";
import {
  formatMessageRecurrenceLabel,
  formatSampleDateLabel,
  MESSAGE_RECURRENCE_OPTIONS,
} from "@/modules/despacho/dueDateRulesHelpers";
import {
  DESPACHO_READ_ONLY_BADGE,
  isDespachoReadOnly,
} from "@/modules/despacho/despachoPermissions";
import { useAuth } from "@/lib/auth";
import { useCapabilities } from "@/lib/useCapabilities";

const FALLBACK_MESSAGE_TYPES = [
  { id: "document_request", label: "Solicitud de documentos", builtin: true },
  { id: "document_reminder", label: "Recordatorio documentos", builtin: true },
  { id: "final_reminder", label: "Recordatorio final", builtin: true },
  { id: "obligation_presented", label: "Obligación presentada", builtin: true },
  { id: "custom", label: "Personalizado", builtin: true },
];

const emptyForm = {
  template_name: "",
  message_type: "document_request",
  body: "",
  recurrence_mode: "monthly",
  recurrence_day_of_month: 5,
  recurrence_month: 1,
  active: true,
};

function messageTypeLabel(types, typeId) {
  return types.find((o) => o.id === typeId)?.label || typeId || "—";
}

export default function CalendarioClientTemplatesPanel({
  testId = "calendario-client-templates",
  section = "all",
}) {
  const showTypes = section === "all" || section === "types";
  const showTemplates = section === "all" || section === "templates";
  const { user } = useAuth();
  const { permissions, role, isPlatformDesigner, loading: capabilitiesLoading } = useCapabilities();
  const readOnly = isDespachoReadOnly({ permissions, role, isPlatformDesigner, capabilitiesLoading, user });

  const [items, setItems] = useState([]);
  const [messageTypes, setMessageTypes] = useState(FALLBACK_MESSAGE_TYPES);
  const [loadState, setLoadState] = useState("loading");
  const [loadMessage, setLoadMessage] = useState("");
  const [editRow, setEditRow] = useState(null);
  const [formOpen, setFormOpen] = useState(false);
  const [form, setForm] = useState(emptyForm);
  const [busy, setBusy] = useState(false);
  const [datePreview, setDatePreview] = useState(null);
  const [datePreviewLoading, setDatePreviewLoading] = useState(false);

  const [typeFormOpen, setTypeFormOpen] = useState(false);
  const [typeEditRow, setTypeEditRow] = useState(null);
  const [typeLabel, setTypeLabel] = useState("");

  const loadTypes = useCallback(async () => {
    try {
      const rows = await despachoFiscalInfoApi.listClientMessageTypes();
      if (rows.length) setMessageTypes(rows);
    } catch {
      setMessageTypes(FALLBACK_MESSAGE_TYPES);
    }
  }, []);

  const load = useCallback(async () => {
    setLoadState("loading");
    setLoadMessage("");
    try {
      if (showTemplates) {
        const [rows] = await Promise.all([
          despachoFiscalInfoApi.listMessageTemplates(),
          loadTypes(),
        ]);
        setItems(rows);
      } else {
        await loadTypes();
        setItems([]);
      }
      setLoadState("ready");
    } catch (err) {
      setItems([]);
      setLoadState(parseFiscalInfoError(err).kind === "forbidden" ? "forbidden" : "error");
      setLoadMessage(parseFiscalInfoError(err).message);
    }
  }, [loadTypes, showTemplates]);

  useEffect(() => {
    load();
  }, [load]);

  const refreshDatePreview = useCallback(async (draft) => {
    setDatePreviewLoading(true);
    try {
      const preview = await despachoFiscalInfoApi.previewMessageTemplateSendDate({
        recurrence_mode: draft.recurrence_mode || "monthly",
        recurrence_day_of_month: Number(draft.recurrence_day_of_month) || 5,
        recurrence_month: draft.recurrence_mode === "annual" ? Number(draft.recurrence_month) || 1 : undefined,
      });
      setDatePreview(preview);
    } catch {
      setDatePreview(null);
    } finally {
      setDatePreviewLoading(false);
    }
  }, []);

  useEffect(() => {
    if (!formOpen) {
      setDatePreview(null);
      return undefined;
    }
    const timer = setTimeout(() => refreshDatePreview(form), 250);
    return () => clearTimeout(timer);
  }, [formOpen, form.recurrence_mode, form.recurrence_day_of_month, form.recurrence_month, refreshDatePreview]);

  const openCreate = () => {
    setEditRow(null);
    setForm(emptyForm);
    setFormOpen(true);
  };

  const openEdit = (row) => {
    const isGlobal = row.owner_id === "__global__";
    if (isGlobal) {
      toast.message("Las plantillas globales son solo lectura. Agregue una plantilla propia del despacho.");
      return;
    }
    setEditRow(row);
    setForm({
      template_name: row.template_name || "",
      message_type: row.message_type || "custom",
      body: row.body || "",
      recurrence_mode: row.recurrence_mode || "monthly",
      recurrence_day_of_month: row.recurrence_day_of_month ?? 5,
      recurrence_month: row.recurrence_month ?? 1,
      active: row.active !== false,
    });
    setFormOpen(true);
  };

  const save = async (event) => {
    event?.preventDefault?.();
    if (!form.template_name.trim() || !form.body.trim()) {
      toast.error("Nombre y mensaje son obligatorios.");
      return;
    }
    const typeOk = messageTypes.some((t) => t.id === form.message_type);
    if (!typeOk) {
      toast.error("Seleccione un tipo de mensaje válido.");
      return;
    }
    setBusy(true);
    try {
      const payload = {
        template_name: form.template_name.trim(),
        message_type: form.message_type,
        body: form.body.trim(),
        recurrence_mode: form.recurrence_mode,
        recurrence_day_of_month: Number(form.recurrence_day_of_month) || 5,
        recurrence_month: form.recurrence_mode === "annual" ? Number(form.recurrence_month) || 1 : null,
        active: form.active,
      };
      if (editRow?.id) {
        await despachoFiscalInfoApi.putMessageTemplate(editRow.id, payload);
        toast.success("Plantilla actualizada.");
      } else {
        await despachoFiscalInfoApi.createMessageTemplate(payload);
        toast.success("Plantilla agregada.");
      }
      setEditRow(null);
      setForm(emptyForm);
      setFormOpen(false);
      await load();
    } catch (err) {
      toast.error(parseFiscalInfoError(err).message);
    } finally {
      setBusy(false);
    }
  };

  const remove = async (row) => {
    if (readOnly || row.owner_id === "__global__") return;
    if (!window.confirm(`¿Eliminar la plantilla «${row.template_name}»?`)) return;
    setBusy(true);
    try {
      await despachoFiscalInfoApi.deleteMessageTemplate(row.id);
      toast.success("Plantilla eliminada.");
      await load();
    } catch (err) {
      toast.error(parseFiscalInfoError(err).message);
    } finally {
      setBusy(false);
    }
  };

  const openTypeCreate = () => {
    setTypeEditRow(null);
    setTypeLabel("");
    setTypeFormOpen(true);
  };

  const openTypeEdit = (row) => {
    if (row.builtin) {
      toast.message("Los tipos incorporados no se pueden editar.");
      return;
    }
    setTypeEditRow(row);
    setTypeLabel(row.label || "");
    setTypeFormOpen(true);
  };

  const saveType = async (event) => {
    event?.preventDefault?.();
    if (!typeLabel.trim()) {
      toast.error("Indique el nombre del tipo.");
      return;
    }
    setBusy(true);
    try {
      let nextTypes = messageTypes;
      if (typeEditRow?.id) {
        const data = await despachoFiscalInfoApi.putClientMessageType(typeEditRow.id, { label: typeLabel.trim() });
        nextTypes = Array.isArray(data?.items) ? data.items : nextTypes;
        toast.success("Tipo actualizado.");
      } else {
        const data = await despachoFiscalInfoApi.createClientMessageType({ label: typeLabel.trim() });
        nextTypes = Array.isArray(data?.items) ? data.items : nextTypes;
        if (data?.item?.id && formOpen) {
          setForm((f) => ({ ...f, message_type: data.item.id }));
        }
        toast.success("Tipo agregado.");
      }
      if (nextTypes.length) setMessageTypes(nextTypes);
      else await loadTypes();
      setTypeFormOpen(false);
      setTypeEditRow(null);
      setTypeLabel("");
    } catch (err) {
      toast.error(parseFiscalInfoError(err).message);
    } finally {
      setBusy(false);
    }
  };

  const removeType = async (row) => {
    if (readOnly || row.builtin) return;
    if (!window.confirm(`¿Eliminar el tipo «${row.label}»?`)) return;
    setBusy(true);
    try {
      await despachoFiscalInfoApi.deleteClientMessageType(row.id);
      toast.success("Tipo eliminado.");
      await loadTypes();
    } catch (err) {
      toast.error(parseFiscalInfoError(err).message);
    } finally {
      setBusy(false);
    }
  };

  const typeColumns = useMemo(
    () => [
      { key: "label", label: "Tipo de mensaje" },
      {
        key: "origin",
        label: "Origen",
        render: (r) => (r.builtin ? "Incorporado" : "Despacho"),
      },
      {
        key: "actions",
        label: "Acción",
        render: (r) => {
          if (readOnly) {
            return <span className="text-[10px] text-slate-400">Solo lectura</span>;
          }
          const locked = Boolean(r.builtin);
          return (
            <div className="flex flex-wrap gap-1">
              <Button
                type="button"
                size="sm"
                variant="outline"
                className="rounded-sm h-7 text-[11px]"
                disabled={locked || busy}
                title={locked ? "Tipo incorporado — no editable" : "Editar tipo"}
                onClick={() => openTypeEdit(r)}
              >
                <Pencil className="h-3 w-3 mr-1" aria-hidden />
                Editar
              </Button>
              <Button
                type="button"
                size="sm"
                variant="ghost"
                className="rounded-sm h-7 text-[11px] text-rose-700 disabled:text-slate-400"
                disabled={locked || busy}
                title={locked ? "Tipo incorporado — no se puede eliminar" : "Eliminar si ya no lo necesita"}
                onClick={() => removeType(r)}
              >
                <Trash2 className="h-3 w-3 mr-1" aria-hidden />
                Eliminar
              </Button>
            </div>
          );
        },
      },
    ],
    [readOnly, busy],
  );

  const columns = useMemo(
    () => [
      { key: "template_name", label: "Nombre" },
      {
        key: "message_type",
        label: "Tipo",
        render: (r) => messageTypeLabel(messageTypes, r.message_type),
      },
      {
        key: "schedule",
        label: "Repetición",
        render: (r) => formatMessageRecurrenceLabel(r),
      },
      {
        key: "sample_send_date",
        label: "Próximo envío (ej.)",
        render: (r) => {
          const nominal = formatSampleDateLabel(r.sample_nominal_send_date);
          const adjusted = formatSampleDateLabel(r.sample_send_date);
          if (adjusted === "—") return "—";
          if (r.sample_send_adjusted && nominal !== adjusted) {
            return (
              <span className="text-xs" title={`Nominal ${nominal} → hábil ${adjusted}`}>
                {adjusted}
                <span className="text-slate-400 ml-1">(desde {nominal})</span>
              </span>
            );
          }
          return <span className="text-xs">{adjusted}</span>;
        },
      },
      {
        key: "body",
        label: "Mensaje",
        render: (r) => (
          <span className="text-xs text-slate-600 line-clamp-2 max-w-xs block whitespace-pre-wrap">{r.body || "—"}</span>
        ),
      },
      {
        key: "actions",
        label: "Acción",
        render: (r) => {
          if (readOnly) {
            return <span className="text-[10px] text-slate-400">Solo lectura</span>;
          }
          const locked = r.owner_id === "__global__";
          return (
            <div className="flex flex-wrap gap-1">
              <Button
                type="button"
                size="sm"
                variant="outline"
                className="rounded-sm h-7 text-[11px]"
                disabled={locked || busy}
                title={locked ? "Plantilla global — agregue una propia del despacho" : "Editar plantilla"}
                onClick={() => openEdit(r)}
              >
                <Pencil className="h-3 w-3 mr-1" aria-hidden />
                Editar
              </Button>
              <Button
                type="button"
                size="sm"
                variant="ghost"
                className="rounded-sm h-7 text-[11px] text-rose-700 disabled:text-slate-400"
                disabled={locked || busy}
                title={locked ? "Plantilla global — no se puede eliminar" : "Eliminar plantilla"}
                onClick={() => remove(r)}
              >
                <Trash2 className="h-3 w-3 mr-1" aria-hidden />
                Eliminar
              </Button>
            </div>
          );
        },
      },
    ],
    [readOnly, busy, messageTypes],
  );

  return (
    <div className="space-y-4" data-testid={testId}>
      <DespachoInfoCallout testId={`${testId}-notice`}>
        {showTemplates && !showTypes ? (
          <>
            Defina <strong>plantillas</strong> de mensaje con repetición mensual o anual para sus clientes.
            Las fechas de envío se ajustan a días hábiles del despacho. Hoy el canal es manual (WhatsApp o correo);
            el envío automático por WhatsApp Business se habilitará en la pestaña <strong>Canal WhatsApp</strong>.
            La ejecución por empresa se gestiona en <strong>Bandeja de envío</strong> (Recordatorios a Clientes).
          </>
        ) : showTypes && !showTemplates ? (
          <>
            Clasifique cada plantilla antes del texto del mensaje. Los tipos incorporados no se pueden modificar.
            Las plantillas recurrentes se configuran en <strong>Plantillas programadas</strong>.
          </>
        ) : (
          <>
            Defina <strong>tipos</strong> y <strong>plantillas</strong> de mensaje con repetición mensual o anual.
            Las fechas de envío se ajustan a días hábiles del despacho, festivos federales e inhábiles configurados.
            La ejecución por empresa y la decisión del auxiliar se gestionan en <strong>Bandeja de envío</strong>.
          </>
        )}
      </DespachoInfoCallout>

      {showTypes ? (
      <DespachoSectionCard title="Tipos de mensaje">
        <div className="flex flex-wrap items-center justify-between gap-2 mb-3">
          <p className="text-xs text-slate-500 m-0">
            Clasifique cada plantilla antes del texto del mensaje. Los tipos incorporados no se pueden modificar.
          </p>
          {!readOnly ? (
            <Button type="button" size="sm" variant="outline" className="rounded-sm h-8 shrink-0" onClick={openTypeCreate}>
              <Plus className="h-3.5 w-3.5 mr-1" aria-hidden />
              Agregar tipo
            </Button>
          ) : null}
        </div>
        {loadState === "loading" ? (
          <div className="flex items-center gap-2 text-sm text-slate-600 py-4">
            <Loader2 className="h-4 w-4 animate-spin" aria-hidden />
            Cargando tipos…
          </div>
        ) : (
        <DespachoSimpleTable columns={typeColumns} rows={messageTypes} emptyLabel="Sin tipos de mensaje." />
        )}
      </DespachoSectionCard>
      ) : null}

      {showTemplates ? (
      <DespachoSectionCard title="Plantillas de mensaje">
        <div className="flex flex-wrap items-center justify-between gap-2 mb-3">
          {readOnly ? (
            <p className="text-xs text-slate-500 m-0">{DESPACHO_READ_ONLY_BADGE} — solo lectura.</p>
          ) : (
            <p className="text-xs text-slate-500 m-0">Mensajes recurrentes al cliente con fecha validada.</p>
          )}
          {!readOnly ? (
            <Button type="button" size="sm" className="rounded-sm bg-[#0F2D5C] hover:bg-[#0a2248] h-8 shrink-0" onClick={openCreate}>
              <Plus className="h-3.5 w-3.5 mr-1" aria-hidden />
              Agregar plantilla
            </Button>
          ) : null}
        </div>

        {loadState === "loading" ? (
          <div className="flex items-center gap-2 text-sm text-slate-600 py-4">
            <Loader2 className="h-4 w-4 animate-spin" aria-hidden />
            Cargando plantillas…
          </div>
        ) : null}

        {loadState === "error" || loadState === "forbidden" ? (
          <div className="rounded-sm border border-rose-200 bg-rose-50 px-4 py-3 text-sm text-rose-950 flex items-start gap-2">
            <AlertCircle className="h-4 w-4 shrink-0 mt-0.5" aria-hidden />
            <p className="m-0">{loadMessage}</p>
          </div>
        ) : null}

        {loadState === "ready" ? (
          <DespachoSimpleTable columns={columns} rows={items} emptyLabel="Sin plantillas de mensaje. Agregue la primera." />
        ) : null}
      </DespachoSectionCard>
      ) : null}

      <Dialog
        open={formOpen}
        onOpenChange={(open) => {
          setFormOpen(open);
          if (!open) {
            setEditRow(null);
            setForm(emptyForm);
          }
        }}
      >
        <DialogContent className="rounded-sm sm:max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{editRow ? "Editar plantilla" : "Agregar plantilla de mensaje"}</DialogTitle>
            <DialogDescription>
              Seleccione el tipo, la repetición y el mensaje. La fecha se valida contra días hábiles y festivos.
            </DialogDescription>
          </DialogHeader>
          <form
            className="space-y-3 py-2"
            onSubmit={save}
          >
            <div className="space-y-1.5">
              <Label className="text-xs">Nombre</Label>
              <Input className="rounded-sm h-9" value={form.template_name} onChange={(e) => setForm((f) => ({ ...f, template_name: e.target.value }))} />
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs">Tipo de mensaje</Label>
              <Select value={form.message_type} onValueChange={(v) => setForm((f) => ({ ...f, message_type: v }))}>
                <SelectTrigger className="rounded-sm"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {messageTypes.map((o) => (
                    <SelectItem key={o.id} value={o.id}>{o.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs">Forma repetitiva</Label>
              <Select value={form.recurrence_mode} onValueChange={(v) => setForm((f) => ({ ...f, recurrence_mode: v }))}>
                <SelectTrigger className="rounded-sm"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {MESSAGE_RECURRENCE_OPTIONS.map((o) => (
                    <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className={`grid gap-3 ${form.recurrence_mode === "annual" ? "grid-cols-2" : "grid-cols-1"}`}>
              <div className="space-y-1.5">
                <Label className="text-xs">{form.recurrence_mode === "annual" ? "Día del mes" : "Día de envío (cada mes)"}</Label>
                <Input type="number" min={1} max={31} className="rounded-sm h-9" value={form.recurrence_day_of_month} onChange={(e) => setForm((f) => ({ ...f, recurrence_day_of_month: e.target.value }))} />
              </div>
              {form.recurrence_mode === "annual" ? (
                <div className="space-y-1.5">
                  <Label className="text-xs">Mes (1–12)</Label>
                  <Input type="number" min={1} max={12} className="rounded-sm h-9" value={form.recurrence_month} onChange={(e) => setForm((f) => ({ ...f, recurrence_month: e.target.value }))} />
                </div>
              ) : null}
            </div>
            <div className="rounded-sm border border-slate-200 bg-slate-50 px-3 py-2 text-xs text-slate-700">
              {datePreviewLoading ? (
                <span className="inline-flex items-center gap-1"><Loader2 className="h-3 w-3 animate-spin" /> Validando fecha…</span>
              ) : datePreview?.sample_send_date ? (
                <>
                  <strong>Próximo envío estimado:</strong>{" "}
                  {formatSampleDateLabel(datePreview.sample_send_date)}
                  {datePreview.sample_send_adjusted ? (
                    <span className="text-slate-500">
                      {" "}(ajustado desde {formatSampleDateLabel(datePreview.sample_nominal_send_date)} por inhábil o festivo)
                    </span>
                  ) : (
                    <span className="text-slate-500"> (sin ajuste — día hábil)</span>
                  )}
                </>
              ) : (
                "Indique día y mes para validar el envío contra días hábiles."
              )}
            </div>
            <MessageEmojiTextarea
              label="Texto del mensaje"
              value={form.body}
              onChange={(body) => setForm((f) => ({ ...f, body }))}
              placeholder="Use {{cliente}}, {{periodo}}, {{obligacion}}, {{fecha_fiscal}}…"
              testId={`${testId}-body`}
            />
            <DialogFooter className="px-0 pb-0">
              <Button type="button" variant="outline" className="rounded-sm" onClick={() => { setFormOpen(false); setEditRow(null); setForm(emptyForm); }}>Cancelar</Button>
              <Button type="submit" className="rounded-sm bg-[#0F2D5C] hover:bg-[#0a2248]" disabled={busy}>
                {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : "Guardar"}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      <Dialog open={typeFormOpen} onOpenChange={setTypeFormOpen}>
        <DialogContent className="rounded-sm sm:max-w-md">
          <DialogHeader>
            <DialogTitle>{typeEditRow ? "Editar tipo de mensaje" : "Agregar tipo de mensaje"}</DialogTitle>
            <DialogDescription>Este tipo aparecerá en el selector antes del texto de cada plantilla.</DialogDescription>
          </DialogHeader>
          <form className="space-y-3 py-2" onSubmit={saveType}>
            <div className="space-y-1.5">
              <Label className="text-xs">Nombre del tipo</Label>
              <Input className="rounded-sm h-9" value={typeLabel} onChange={(e) => setTypeLabel(e.target.value)} placeholder="Ej. Recordatorio de pagos" />
            </div>
            <DialogFooter className="px-0 pb-0">
              <Button type="button" variant="outline" className="rounded-sm" onClick={() => setTypeFormOpen(false)}>Cancelar</Button>
              <Button type="submit" className="rounded-sm bg-[#0F2D5C] hover:bg-[#0a2248]" disabled={busy}>
                {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : "Guardar"}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
